import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi, errMsg, notifyRefresh } from "@/lib/api";
import { METHODS, brl, todayISO } from "@/lib/constants";

export const PaymentDialog = ({ open, onOpenChange, patientId, charge }) => {
  const api = useApi();
  const [f, setF] = useState({ amount: "", method: "pix", date: todayISO(), notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF({ amount: charge ? charge.remaining : "", method: "pix", date: todayISO(), notes: "" });
  }, [open, charge]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/payments`, { ...f, amount: Number(f.amount), charge_id: charge?.id || null });
      toast.success("Pagamento registrado");
      notifyRefresh();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Registrar pagamento</DialogTitle>
        </DialogHeader>
        {charge && (
          <div className="rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
            {charge.patient_name && <p className="font-semibold">{charge.patient_name}</p>}
            <p>{charge.description}</p>
            <p className="text-xs text-sky-700">Em aberto: {brl(charge.remaining)}</p>
          </div>
        )}
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" min="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Data</Label>
              <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="payment-date-input" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Forma de pagamento</Label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {Object.entries(METHODS).map(([k, v]) => (
                <button type="button" key={k} onClick={() => setF({ ...f, method: k })} data-testid={`payment-method-${k}`}
                  className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${f.method === k ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:border-sky-200"}`}>
                  {v.replace("Cartão de ", "")}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Observação</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Ex.: comprovante PIX recebido" data-testid="payment-notes-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="payment-save-btn">
              {saving ? "Salvando..." : "Confirmar pagamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
